import React, { useEffect } from "react";
import { Scrollbars } from "react-custom-scrollbars-4";
import { useSnackbar } from "notistack";
import {
  CircularProgress,
  Backdrop,
  Container,
  Stack,
  Breadcrumbs,
  Toolbar,
  Typography,
  Paper,
  Link,
} from "@mui/material";
import { getSchema } from "../helpers/schemas/getSchema";
import FeatureFields from "../Feature/FeatureFields";
import { useNavigate } from "react-router-dom";
import ViewFiles from "../Dialogs/ViewFiles";
import IssuesList from "../Issues/IssuesList";
import EquipmentEdit from "../Equipment/EquipmentEdit";
import CommentsList from "../Comments/CommentsList";
import ChecklistsList from "../Checklists/ChecklistsList";
import { getUserAccess } from "../helpers/getUserAccess";
import { getProject } from "../helpers/getProject";
import ExportButton from "../Dialogs/ExportButton";

export default function Equipment(props) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = React.useState(true);
  const [isReady, setIsReady] = React.useState(false);
  const [project, setProject] = React.useState({});
  const [equipment, setEquipment] = React.useState({});
  const [schema, setSchema] = React.useState([]);
  const [editingEquipment, setEditingEquipment] = React.useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const params = window.location.href.split("/");

        const proj = await getProject(params[4]);
        setIsLoading(false);
        setSchema(getSchema("equipment"));

        const pathSegments = window.location.pathname.split("/");
        const obs = proj.Equipment.find((o) => o.id === pathSegments[4]);
        setEquipment(obs);
        setProject(proj);
      } catch (error) {
        console.error("Error getting document:", error);
        enqueueSnackbar(error.message, {
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
          variant: "error",
        });
      }
    };

    if (isLoading === true && isReady === false) {
      setIsReady(true);
      fetchData();
    }
  });

  const onEditClick = () => {
    setEditingEquipment(!editingEquipment);
  };

  const passUpProject = (proj) => {
    setProject({
      ...proj,
    });
    setEquipment({
      ...proj.Equipment.find((o) => o.id === equipment.id),
    });
    setEditingEquipment(false);
  };

  return isLoading ? (
    <Backdrop
      sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      open={isLoading}
    >
      <CircularProgress color="inherit" />
    </Backdrop>
  ) : (
    <Scrollbars
      autoHide
      autoHideTimeout={10}
      style={{ width: "100%", height: "100%" }}
    >
      <Toolbar />
      <Container
        maxWidth="xl"
        style={{
          // paddingTop: 30,
          paddingBottom: 30,
          // paddingBottom: theme.spacing(4),
          justifyContent: "center",
        }}
      >
        <Typography
          color="primary.light"
          style={{ paddingTop: 40 }}
          variant="h5"
          gutterBottom
        >
          {`Equipment Name: ${equipment.Name}`}
        </Typography>
        <Stack
          sx={{
            justifyContent: "center",
            alignItems: "center",
          }}
          spacing={2}
        >
          <Breadcrumbs separator="›" aria-label="breadcrumb">
            <Link
              underline="hover"
              key="1"
              color="inherit"
              href=""
              onClick={() =>
                navigate(`/project/${project.id}/dashboard`, {
                  state: { passProject: project },
                })
              }
            >
              Project: {project.Name}
            </Link>
            ,
            <Link
              underline="hover"
              key="2"
              color="inherit"
              href={`/project/${project.id}/assets`}

              // onClick={handleClick}
            >
              {`Assets`}
            </Link>
            ,
            <Typography
              underline="hover"
              key="2"
              color="inherit"
              href={`/project/${project.id}/equipment`}

              // onClick={handleClick}
            >
              {`Equipment Name: ${equipment.Name}`}
            </Typography>
            ,
          </Breadcrumbs>
        </Stack>

        <Stack
          // style={{ paddingTop: 10 }}
          direction="row"
          spacing={2}
          sx={{
            justifyContent: "flex-end",
            alignItems: "flex-end",
          }}
        >
          <ExportButton
            issue={null}
            observation={null}
            equipment={equipment}
            project={project}
            exportTableName="Equipment"
          />
        </Stack>

        <Paper elevation={6} style={{ marginTop: 30 }}>
          <div style={{ padding: 16 }}>
            <FeatureFields
              allowUpdate={
                getUserAccess(props.user, project.uid, project.Contacts)
                  .allowUpdate
              }
              TableName={"Equipment"}
              feature={equipment}
              schema={schema}
              key={0}
              onEditClick={onEditClick}
            />
          </div>
        </Paper>
        <Typography
          color="primary.light"
          style={{ paddingTop: 40 }}
          variant="h5"
          gutterBottom
        >
          Equipment Files
        </Typography>
        <ViewFiles
          observationId={null}
          equipmentId={equipment.id}
          onEditClick={onEditClick}
          viewFiles={project.Files.filter(
            (i) => i.equipment_id === equipment.id,
          )}
        />

        <Typography
          color="primary.light"
          style={{ paddingTop: 40 }}
          variant="h5"
          gutterBottom
        >
          {`Equipment Checklists`}
        </Typography>

        {project !== null && (
          <ChecklistsList
            user={props.user}
            project={project}
            observationId={null}
            equipmentId={equipment.id}
            issueId={null}
            passUpProject={passUpProject}
            saveCurrentProject={props.saveCurrentProject}
          />
        )}
        <Typography
          color="primary.light"
          style={{ paddingTop: 40 }}
          variant="h5"
          gutterBottom
        >
          Equipment Comments
        </Typography>

        {project !== null && (
          <CommentsList
            user={props.user}
            project={project}
            observationId={null}
            equipmentId={equipment.id}
            issueId={null}
            passUpProject={passUpProject}
            saveCurrentProject={props.saveCurrentProject}
          />
        )}

        <Typography
          color="primary.light"
          style={{ paddingTop: 40 }}
          variant="h5"
          gutterBottom
        >
          Equipment Issues
        </Typography>

        {project !== null && (
          <IssuesList
            user={props.user}
            project={project}
            observationId={null}
            equipmentId={equipment.id}
            passUpProject={passUpProject}
            saveCurrentProject={props.saveCurrentProject}
          />
        )}
        {editingEquipment && (
          <EquipmentEdit
            equipment={equipment}
            project={project}
            passUpProject={passUpProject}
            user={props.user}
            onEditClick={onEditClick}
          />
        )}
      </Container>
    </Scrollbars>
  );
}
